import { useState } from 'react';
import { Clock, User, Check, X } from 'lucide-react';
import { Prompt } from '@/types';
import { deployPromptVersion } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { formatDate, cn } from '@/lib/utils';

interface PromptVersionHistoryProps {
  promptName: string;
  currentVersion: string;
  versions: Prompt[];
  agentId: string;
  onClose: () => void;
  onUpdate?: () => void;
}

export function PromptVersionHistory({
  promptName,
  currentVersion,
  versions,
  agentId,
  onClose,
  onUpdate,
}: PromptVersionHistoryProps) {
  const [deploying, setDeploying] = useState<string | null>(null);

  const handleDeploy = async (version: string) => {
    setDeploying(version);
    try {
      await deployPromptVersion(agentId, promptName, version);
      onUpdate?.();
    } catch (err) {
      console.error('Deploy failed:', err);
    } finally {
      setDeploying(null);
    }
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display font-semibold text-lg">版本歷史</h3>
          <p className="text-sm text-muted-foreground">{promptName}</p>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Versions List */}
      <div className="space-y-3">
        {versions.map((version) => {
          const isCurrent = version.version === currentVersion;
          const isDeploying = deploying === version.version;

          return (
            <Card
              key={version.version}
              className={cn(
                "transition-all duration-200",
                isCurrent && "border-primary/50 bg-primary/5"
              )}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge
                        variant={isCurrent ? "default" : "secondary"}
                        className="shrink-0"
                      >
                        v{version.version}
                      </Badge>
                      {isCurrent && (
                        <Badge variant="status-running" className="shrink-0">
                          使用中
                        </Badge>
                      )}
                      {version.status === 'draft' && (
                        <Badge variant="status-draft" className="shrink-0">
                          草稿
                        </Badge>
                      )}
                    </div>

                    {version.versionNote && (
                      <p className="text-sm mb-2">{version.versionNote}</p>
                    )}

                    <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(version.createdAt)}
                      </span>
                      {version.createdBy && (
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {version.createdBy}
                        </span>
                      )}
                      <span>{version.content.length} 字元</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {!isCurrent && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeploy(version.version)}
                        disabled={isDeploying}
                      >
                        {isDeploying ? (
                          <>
                            <Clock className="h-3 w-3 mr-1 animate-spin" />
                            部署中
                          </>
                        ) : (
                          <>
                            <Check className="h-3 w-3 mr-1" />
                            部署
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Content Preview */}
                <details className="mt-3 group">
                  <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
                    查看內容
                  </summary>
                  <div className="mt-2 p-3 bg-muted/50 rounded-lg text-sm font-mono max-h-48 overflow-y-auto">
                    <pre className="whitespace-pre-wrap break-words">{version.content}</pre>
                  </div>
                </details>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
